import { useEffect, useState } from "react";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { PageHeader, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import StaffIncentiveReport from "./StaffIncentiveReport";
import StaffSummaryReport from "./StaffSummaryReport";
import { istNow, ymd } from "../lib/dates";

const today = () => ymd(istNow());

function DailyAllStaff() {
  const { branchParam } = useBranch();
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [slabs, setSlabs] = useState([]);

  useEffect(() => { api.get("/incentives/settings").then((r) => setSlabs(r.data.slabs)); }, []);
  useEffect(() => {
    api.get("/incentives/daily", { params: { date, branch_id: branchParam } }).then((r) => setRows(r.data));
  }, [date, branchParam]);

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="incentive-date" className="bg-secondary w-44" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {slabs.map((s, i) => (
          <Card key={i} className="p-4 bg-card border-border">
            <p className="text-xs text-muted-foreground">{rupee(s.min)}{s.max ? ` – ${rupee(s.max)}` : "+"}</p>
            <p className="font-display text-3xl text-primary mt-1">{s.percent}%</p>
          </Card>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mb-2" data-testid="incentive-audit-note">
        How it's calculated: <span className="text-primary">Eligible Base</span> = Service Billing (+ Product Billing only if products are enabled) → matched to a slab % → Incentive = Eligible Base × Slab %.
      </p>
      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Staff</TableHead><TableHead>Date</TableHead>
              <TableHead className="text-right">Service Billing</TableHead>
              <TableHead className="text-right">Product Billing</TableHead>
              <TableHead className="text-right">Eligible Base</TableHead>
              <TableHead className="text-right">Slab %</TableHead>
              <TableHead className="text-right">Incentive</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="border-border" data-testid={`incentive-row-${r.id}`}>
                <TableCell className="font-medium">{r.staff_name}</TableCell>
                <TableCell>{r.date}</TableCell>
                <TableCell className="text-right">{rupee(r.service_billing ?? r.eligible_billing)}</TableCell>
                <TableCell className="text-right text-muted-foreground">{rupee(r.product_billing || 0)}</TableCell>
                <TableCell className="text-right">{rupee(r.eligible_billing)}</TableCell>
                <TableCell className="text-right"><Badge variant="outline" className="border-primary text-primary">{r.percent}%</Badge></TableCell>
                <TableCell className="text-right text-primary font-medium" title={`${rupee(r.eligible_billing)} × ${r.percent}%`}>{rupee(r.incentive_amount)}</TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No incentive data for this date.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export default function Incentives() {
  return (
    <div>
      <PageHeader title="Staff Incentives" subtitle="Slab-based on each staff's total eligible daily billing — separate from attendance" />
      <Tabs defaultValue="individual" className="w-full">
        <TabsList className="mb-5">
          <TabsTrigger value="individual" data-testid="tab-individual">Individual Staff Report</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary">Staff Incentive Report</TabsTrigger>
          <TabsTrigger value="daily" data-testid="tab-daily">All Staff (Daily)</TabsTrigger>
        </TabsList>
        <TabsContent value="individual"><StaffIncentiveReport /></TabsContent>
        <TabsContent value="summary"><StaffSummaryReport /></TabsContent>
        <TabsContent value="daily"><DailyAllStaff /></TabsContent>
      </Tabs>
    </div>
  );
}
